import { Button } from '@appsumo/dorado-react';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Link from 'next/link';
import { memo, useMemo } from 'react';
import useSWR from 'swr';

import { useUserStatus } from '~/hooks/userStatus';
import { QuestionPageProps } from '~/lib/discussions/types';
import { fetchJson } from '~/lib/fetch';
import { PDPContext } from '~/lib/product/context';
import { useOneTap } from '~/lib/script';
import useUser from '~/lib/user';
import { firstSentence } from '~/lib/util';

import DiscussionSidebar from '~/components/discussions/DiscussionSidebar';
import { QuestionSeo } from '~/components/seo';
import useDealCampaign from '~/hooks/deal-campaign';
import CampaignBanner from '../common/CampaignBanner';
import { Breadcrumb, Breadcrumbs } from '../ui';
import QuestionCard from './card/QuestionCard';
import CommentList from './sidebar/CommentList';

export const QuestionPageComponent = memo(
  ({ deal, discussion: fallbackData, discussionId }: QuestionPageProps) => {
    useOneTap();

    // API V2
    const url = `/api/v2/deals/${deal.id}/questions/${discussionId}/`;

    const { data: commentData } = useSWR(url, fetchJson, {
      fallbackData: fallbackData,
    });
    const comments = commentData?.comments ? commentData.comments : commentData;
    const question = comments[0];

    const { userStatus } = useUserStatus(deal.id);
    const { user } = useUser();
    const { dealActiveAutoCampaign, campaignBannerContent, backgroundColor } =
      useDealCampaign(deal);

    const questionTitle = question.title || question.comment;
    const truncatedTitle = firstSentence({
      text: questionTitle,
      maxLength: 100,
      showEllipsis: true,
    });

    const items: Breadcrumb[] = useMemo(
      () => [
        { href: `/products/${deal.slug}/`, label: deal.public_name },
        {
          href: `/products/${deal.slug}/questions/`,
          label: `${deal.public_name} Questions`,
        },
        { label: truncatedTitle },
      ],
      [deal.public_name, deal.slug, truncatedTitle],
    );

    const memoizedProvider = useMemo(
      () => ({ deal, userStatus, user }),
      [deal, userStatus, user],
    );

    return (
      <>
        <QuestionSeo question={question} deal={deal} />
        <PDPContext.Provider value={memoizedProvider}>
          {!!dealActiveAutoCampaign && campaignBannerContent && (
            <CampaignBanner
              bannerContent={campaignBannerContent}
              backgroundColor={backgroundColor}
              bannerBadge={
                dealActiveAutoCampaign?.campaign_config?.banner_badge
              }
              fontColor={
                dealActiveAutoCampaign?.campaign_config?.banner_font_color
              }
            />
          )}

          <div className="mx-auto mb-8 w-full max-w-7xl grow space-y-4 px-8 md:my-12">
            <Breadcrumbs
              items={items}
              className="mt-6 md:mb-4 lg:mb-6 xl:px-8"
            />

            {/* Mobile deal header */}
            <div className="-mx-8 mb-4 flex flex-wrap justify-between gap-x-4 border-b p-4 px-8 sm:hidden">
              <h3 className="font-header text-2xl font-semibold">
                {deal.public_name}
              </h3>
              <Link
                href={`/products/${deal.slug}/`}
                className="flex items-center text-blue-600"
              >
                Product details
                <FontAwesomeIcon
                  icon={faArrowUpRightFromSquare}
                  className="ml-2"
                />
              </Link>
            </div>
            {/* End Mobile deal header */}

            <article className="mx-auto flex max-w-6xl gap-x-8 max-sm:flex-col">
              <div className="grow">
                <main>
                  <QuestionCard question={question} expandCard />
                </main>
              </div>

              <div className="hidden lg:block">
                <DiscussionSidebar deal={deal} />
              </div>
            </article>

            <div className="space-y-4 lg:hidden">
              <CommentList
                title="Related questions"
                prefix={`/products/${deal.slug}/`}
                comments={deal.top_5_questions}
              />

              <Button
                href={`/products/${deal.slug}/`}
                tertiary
                className="block text-center"
              >
                View product details
              </Button>
            </div>
          </div>
        </PDPContext.Provider>
      </>
    );
  },
);

QuestionPageComponent.displayName = 'QuestionPageComponent';
